import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Settings,
  TrendingUp,
  BarChart,
  Repeat,
  Users,
  Wallet,
  Tag,
  CheckSquare,
  Hand,
  Bot,
  PiggyBank,
  Zap,
  Star,
  ChevronRight,
  Upload,
  LineChart,
} from 'lucide-react';
import logo from '../logo.svg';
import { useUserAccess } from '../hooks/useUserAccess';

type NavLinkItem = {
  type: 'link';
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
};

type NavSectionItem = { type: 'section'; label: string };

type NavEntry = NavLinkItem | NavSectionItem;

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { hasFeature, isFree } = useUserAccess();
  const budgetsEnabled = hasFeature('budgets');
  const assistantEnabled = hasFeature('assistant_ia');

  const navigation = useMemo((): NavEntry[] => {
    const items: NavEntry[] = [
      { type: 'link', name: 'Tableau de bord', href: '/', icon: Home },
      { type: 'section', label: 'Opérations' },
      { type: 'link', name: 'Transactions', href: '/transactions', icon: Zap },
      { type: 'link', name: 'Transactions périodiques', href: '/periodic-transactions', icon: Repeat },
      { type: 'section', label: 'Argent' },
      { type: 'link', name: 'Comptes', href: '/accounts', icon: Wallet },
      { type: 'link', name: 'Catégories', href: '/categories', icon: Tag },
    ];

    if (budgetsEnabled) {
      items.push({ type: 'link', name: 'Budgets', href: '/budgets', icon: PiggyBank });
    }

    items.push(
      { type: 'section', label: 'Analyses' },
      { type: 'link', name: 'Graphiques', href: '/charts', icon: TrendingUp },
      { type: 'link', name: 'Graphiques annuels', href: '/annual-charts', icon: BarChart },
      { type: 'link', name: 'Évolution des soldes', href: '/solde-evolution', icon: LineChart },
      { type: 'section', label: 'Partage & aide' },
      { type: 'link', name: 'Dépenses partagées', href: '/shared-expenses', icon: Users },
      { type: 'link', name: 'Prêts entre amis', href: '/loans', icon: Hand },
    );

    if (assistantEnabled) {
      items.push({
        type: 'link',
        name: 'Assistant IA',
        href: '/analyse',
        icon: Bot,
        badge: 'BETA',
      });
    }

    items.push(
      { type: 'section', label: 'Données & préférences' },
      { type: 'link', name: 'Importer', href: '/import', icon: Upload },
      { type: 'link', name: 'Tâches', href: '/tasks', icon: CheckSquare },
      { type: 'link', name: 'Paramètres', href: '/settings', icon: Settings },
    );

    return items;
  }, [budgetsEnabled, assistantEnabled]);

  function isHrefActive(href: string): boolean {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  }

  return (
    <aside className="relative z-20 flex w-[248px] shrink-0 flex-col border-r border-white/[0.06] bg-dark-960/95 backdrop-blur-xl">
      <div className="px-4 py-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500/10 ring-1 ring-primary-500/20">
            <img src={logo} alt="SuiviFinance" className="h-6 w-6 object-contain" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-[15px] font-bold tracking-tight text-white">SuiviFinance</h1>
            <p className="text-[11px] font-medium text-dark-500">Finance personnelle</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2.5 pb-3">
        {navigation.map((item, index) => {
          if (item.type === 'section') {
            return (
              <div key={`section-${item.label}-${index}`} className="pb-1 pt-4 first:pt-2">
                <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-dark-500">
                  {item.label}
                </p>
              </div>
            );
          }

          const isActive = isHrefActive(item.href);
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={`nav-item ${isActive ? 'nav-item-active' : 'nav-item-idle'}`}
            >
              <item.icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-primary-400' : ''}`} />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              {item.badge && (
                <span className="rounded-md bg-accent-purple/20 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-accent-purple">
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="space-y-3 border-t border-white/[0.06] p-3">
        {isFree && (
          <button
            type="button"
            onClick={() => {
              window.location.href = '/settings';
            }}
            className="flex w-full items-center gap-3 rounded-2xl bg-white/[0.04] p-3.5 text-left ring-1 ring-white/[0.08] transition hover:bg-white/[0.07]"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-amber/15">
              <Star className="h-4 w-4 text-accent-amber" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-white">Passer à Premium</p>
              <p className="text-[11px] text-dark-500">Plus de fonctionnalités</p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-dark-500" />
          </button>
        )}

        <p className="px-1 text-center text-[10px] text-dark-500">Version 1.2.1</p>
      </div>
    </aside>
  );
};

export default Sidebar;
